// src/pages/InvestorRelationsPage.tsx

import React, { FC } from 'react';
import styles from '../css/InvestorRelationsPage.module.css';
import { motion, Variants } from 'framer-motion'; 
import { Link } from 'react-router-dom';
import { 
    ShieldCheck, 
    Globe2, 
    BrainCircuit, 
    TrendingUp, 
    ArrowRight, 
    Check, 
    Clock, 
    Target,
    Server,
    Lock
} from 'lucide-react';

// Shared Components
import AnimatedSection from '../components/Shared/AnimatedSection';

// --- Data Structures ---
interface TrustPoint {
    icon: React.ReactNode;
    title: string;
    description: string;
}

interface RoadmapItem {
    timeframe: string;
    milestone: string;
    details?: string;
    status: 'completed' | 'current' | 'future';
}

const InvestorRelationsPage: FC = () => {
    
    // Data: Governance & Trust (Updated to 6 items)
    const trustPoints: TrustPoint[] = [
        { 
            icon: <ShieldCheck size={24} strokeWidth={1.5} />, 
            title: "US Incorporated Entity", 
            description: "Delaware C-Corp structure ensuring robust legal frameworks, IP protection, and standardized financial governance." 
        },
        { 
            icon: <Globe2 size={24} strokeWidth={1.5} />, 
            title: "Global Board Oversight", 
            description: "Strategic board members across three continents provide essential market insight and regulatory guidance." 
        },
        { 
            icon: <BrainCircuit size={24} strokeWidth={1.5} />, 
            title: "Domain-First Engineering", 
            description: "Our competitive moat is expertise. Solutions are architected by Chartered Accountants, then built by engineers." 
        },
        { 
            icon: <TrendingUp size={24} strokeWidth={1.5} />, 
            title: "Clear Path to Liquidity", 
            description: "Focused venture studio model designed to build independent, valuation-ready asset classes starting with OTA ERP." 
        },
        { 
            icon: <Server size={24} strokeWidth={1.5} />, 
            title: "Cloud-Native Scalability", 
            description: "Serverless AWS infrastructure allows for infinite horizontal scaling with minimal overhead costs." 
        },
        { 
            icon: <Lock size={24} strokeWidth={1.5} />, 
            title: "Data Sovereignty", 
            description: "Strict data residency protocols and GDPR-ready architecture designed for global enterprise adoption." 
        },
    ];

    // Data: Strategic Roadmap
    const roadmap: RoadmapItem[] = [
        { 
            timeframe: "2023 - 2025", 
            milestone: "Foundation & Architecture", 
            details: "Development of OTA ERP V1 Core, ISO-compliant security framework, and initial IP registration.",
            status: 'completed' 
        },
        { 
            timeframe: "Q4 2025 - Q1 2026", 
            milestone: "Beta & Governance", 
            details: "Final Beta testing of OTA ERP. Launch of mobile companion apps. Formal assembly of the Global Strategic Board.",
            status: 'current' 
        },
        { 
            timeframe: "Month 1 (Post-Beta)", 
            milestone: "Market Penetration Campaign", 
            details: "Targeted 3-month rollout to pre-qualified SMEs in logistics and manufacturing sectors.",
            status: 'future' 
        },
        { 
            timeframe: "Month 4-5", 
            milestone: "Commercial Launch", 
            details: "Full public release of OTA ERP with tiered subscription modeling and enterprise API access.",
            status: 'future' 
        },
        { 
            timeframe: "Long Term", 
            milestone: "Portfolio Expansion", 
            details: "Development of OTA ERP V2 and initiation of specialized LegalTech vertical (Project Suit).",
            status: 'future' 
        },
    ];
    
    // Animation Variants
    const containerVariants: Variants = { 
        visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } } 
    };
    const itemVariants: Variants = { 
        hidden: { opacity: 0, y: 20 }, 
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } 
    };

    return (
        <div className={styles.investorPage}>

            {/* 1. HERO SECTION */}
            <section className={styles.heroSection}>
                <div className={styles.gridBackground}></div>
                <div className={styles.container}>
                    <div className={styles.heroContent}>
                        <motion.div 
                            initial="hidden" animate="visible" variants={containerVariants}
                            className={styles.heroWrapper}
                        >
                            <motion.span variants={itemVariants} className={styles.heroBadge}>
                                Investor Relations
                            </motion.span>
                            
                            <motion.h1 variants={itemVariants} className={styles.heroTitle}>
                                Invest in the <br/>
                                <span className={styles.titleHighlight}>Structural Integrity</span> of <br/>
                                Modern Business.
                            </motion.h1>
                            
                            <motion.p variants={itemVariants} className={styles.heroSubtitle}>
                                OTA TECH Inc. builds high-value, defensible software platforms driven by a unique synthesis of financial rigor and machine learning.
                            </motion.p>
                        </motion.div>
                    </div>
                </div>
                <div className={styles.heroFade}></div>
            </section>

            {/* 2. GOVERNANCE GRID */}
            <AnimatedSection className={styles.governanceSection}>
                <div className={styles.container}>
                    <div className={styles.sectionHeaderCenter}>
                        <span className={styles.sectionLabel}>Why Invest</span>
                        <h2 className={styles.sectionTitle}>Governance & Strategy</h2>
                    </div>
                    
                    <motion.div className={styles.trustGrid} variants={containerVariants}>
                        {trustPoints.map((point, index) => (
                            <motion.div key={index} className={styles.trustCard} variants={itemVariants}>
                                {/* UPDATED LAYOUT: Row based header */}
                                <div className={styles.cardHeader}>
                                    <div className={styles.trustIconWrapper}>{point.icon}</div>
                                    <h3 className={styles.cardTitle}>{point.title}</h3>
                                </div>
                                <p className={styles.cardDesc}>{point.description}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </AnimatedSection>
            
            {/* 3. ROADMAP (Vertical Process Nodes) */}
            <AnimatedSection className={styles.roadmapSection}>
                <div className={styles.container}>
                    <div className={styles.sectionHeaderCenter}>
                        <span className={styles.sectionLabel}>Execution Plan</span>
                        <h2 className={styles.sectionTitle}>Deployment Milestones</h2>
                    </div>

                    <div className={styles.timelineWrapper}>
                        <div className={styles.timelineLine}></div>
                        
                        {roadmap.map((item, index) => (
                            <motion.div 
                                key={index} 
                                className={`${styles.timelineItem} ${styles[item.status]}`}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                {/* The Marker (Circle) */}
                                <div className={styles.markerWrapper}>
                                    <div className={styles.timelineMarker}>
                                        {item.status === 'completed' && <Check size={14} strokeWidth={3} />}
                                        {item.status === 'current' && (
                                            <>
                                                <div className={styles.pulseRing}></div>
                                                <Clock size={14} strokeWidth={3} />
                                            </>
                                        )}
                                        {item.status === 'future' && <Target size={14} strokeWidth={2} />}
                                    </div>
                                </div>

                                {/* The Content Card */}
                                <div className={styles.timelineContent}>
                                    <div className={styles.timelineHeader}>
                                        <span className={styles.timelineTimeframe}>{item.timeframe}</span>
                                        {item.status === 'current' && <span className={styles.statusBadge}>Active Phase</span>}
                                    </div>
                                    <h4 className={styles.timelineMilestone}>{item.milestone}</h4>
                                    <p className={styles.timelineDetails}>{item.details}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </AnimatedSection>
            
            {/* 4. INVESTMENT CTA */}
            <AnimatedSection className={styles.ctaSection}>
                <div className={styles.container}>
                    <motion.div className={styles.ctaCard} variants={itemVariants}>
                        <div className={styles.ctaContent}>
                            <h2>Strategic Partnership</h2>
                            <p>
                                We are actively engaging with strategic investors to scale our portfolio. 
                                Access our full investment thesis and financial projections.
                            </p>
                        </div>
                        <div className={styles.ctaAction}>
                            <Link to="/contact" className={styles.ctaButton}>
                                Request Investor Deck <ArrowRight size={18} />
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </AnimatedSection>

        </div>
    );
};

export default InvestorRelationsPage;